import { ui } from "@/components/ui";
import type { EntryViewT } from "@/types/database/entries";
import type { DueEntryT as TrackerDueEntryT } from "@/types/database/trackers";
import Complete from "../../../../entries/components/Complete";
import type { CombinedEntry, DueEntryT } from "../types";
import { EntriesList } from "./EntriesList";
import EntryDue from "./new-entry-states/EntryDue";
import EntryLocked from "./new-entry-states/EntryLocked";
import EntryReadyToReview from "./new-entry-states/EntryReadyToReview";

interface Props {
	endDate: string | null;
	isComplete: boolean;
	isNextEntryLocked: boolean;
	nextEntryDueAt: string | null;
	dueEntriesCount: number;
	nextEntryLabel: string;
	canMarkComplete?: boolean;
	dueEntries?: (DueEntryT | TrackerDueEntryT | null)[];
	entries?: (EntryViewT | DueEntryT | TrackerDueEntryT | null)[];
}

const DueEntriesInfo = ({
	count,
	dueEntries = [],
	entries = [],
}: {
	count: number;
	dueEntries: (DueEntryT | TrackerDueEntryT | null)[];
	entries: (EntryViewT | DueEntryT | TrackerDueEntryT | null)[];
}) => {
	// Combine and sort entries by schedule_index
	const allEntries = [
		...entries.map((e) => {
			if (!e) return null;
			return {
				...e,
				type: "entry" as const,
				is_completed: true,
				is_locked: false,
				label: e.label ?? `Assessment #${(e.schedule_index ?? 0) + 1}`,
				timing_label: e.timing_label,
			} as CombinedEntry;
		}),
		...dueEntries.map((e) => {
			if (!e) return null;
			return {
				...e,
				type: "due_entry" as const,
				is_completed: false,
				label: e.label ?? `Assessment #${(e.schedule_index ?? 0) + 1}`,
				timing_label: e.timing_label,
			} as CombinedEntry;
		}),
	]
		.filter((e): e is NonNullable<typeof e> => e !== null)
		.sort((a, b) => (a.schedule_index ?? 0) - (b.schedule_index ?? 0));

	return <EntriesList entries={allEntries} count={count} />;
};

const NewStateSwitch = ({
	endDate,
	isComplete,
	isNextEntryLocked,
	nextEntryDueAt,
	dueEntriesCount,
	nextEntryLabel,
	canMarkComplete = false,
	dueEntries = [],
	entries = [],
}: Props) => {
	if (endDate !== null) {
		return <Complete />;
	}

	if (isComplete) {
		return <EntryReadyToReview canMarkComplete={canMarkComplete} />;
	}

	if (isNextEntryLocked) {
		return (
			<div className="flex flex-col gap-2 w-full">
				<EntryLocked
					nextEntryDueAt={nextEntryDueAt ?? new Date().toISOString()}
				/>
				{dueEntriesCount > 0 && (
					<DueEntriesInfo
						count={dueEntriesCount}
						dueEntries={dueEntries}
						entries={entries}
					/>
				)}
			</div>
		);
	}

	if (!isNextEntryLocked) {
		return dueEntriesCount + Number(entries.length) > 0 ? (
			<DueEntriesInfo
				count={dueEntriesCount}
				dueEntries={dueEntries}
				entries={entries}
			/>
		) : (
			<EntryDue dueEntries={dueEntriesCount} nextEntryLabel={nextEntryLabel} />
		);
	}

	return (
		<ui.Alert variant="destructive">
			<ui.AlertTitle>Error</ui.AlertTitle>
			<ui.AlertDescription>
				Something went wrong with this entry state
			</ui.AlertDescription>
		</ui.Alert>
	);
};

export default NewStateSwitch;
